import React, { useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Select,
  Button,
  Icon,
  Spinner,
  Center,
} from '@chakra-ui/react'
import {
  FiFile,
  FiFolder,
  FiLock,
  FiMail,
  FiUpload,
  FiTrash2,
  FiRotateCcw,
  FiEdit2,
  FiMove,
  FiPlusCircle,
  FiActivity,
  FiEye,
  FiShare2,
  FiTag,
  FiMessageSquare,
  FiCompass,
} from 'react-icons/fi'
import { AuditLogEntry, AuditLogFilters } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface AuditLogContentProps {
  entries: AuditLogEntry[]
  isLoading: boolean
  filters: AuditLogFilters
  onFilterChange: (filters: AuditLogFilters) => void
  pagination: { current_page: number; last_page: number; total: number }
  onLoadMore: () => void
}

const ACTION_OPTIONS = [
  'auth.login',
  'auth.logout',
  'auth.password_changed',
  'file.uploaded',
  'file.renamed',
  'file.moved',
  'file.deleted',
  'file.restored',
  'file.viewed',
  'file.shared',
  'file.tag_added',
  'folder.created',
  'folder.renamed',
  'folder.moved',
  'folder.deleted',
  'folder.visited',
  'folder.shared',
  'chat.query',
  'navigation.recently_viewed',
  'navigation.favorites',
  'navigation.shared_with_me',
  'invitation.sent',
]

const ACTION_ICONS: Record<string, React.ElementType> = {
  'auth.login': FiLock,
  'auth.logout': FiLock,
  'auth.password_changed': FiLock,
  'file.uploaded': FiUpload,
  'file.renamed': FiEdit2,
  'file.moved': FiMove,
  'file.deleted': FiTrash2,
  'file.restored': FiRotateCcw,
  'file.viewed': FiEye,
  'file.shared': FiShare2,
  'file.tag_added': FiTag,
  'folder.created': FiPlusCircle,
  'folder.renamed': FiEdit2,
  'folder.moved': FiMove,
  'folder.deleted': FiTrash2,
  'folder.visited': FiFolder,
  'folder.shared': FiShare2,
  'chat.query': FiMessageSquare,
  'navigation.recently_viewed': FiCompass,
  'navigation.favorites': FiCompass,
  'navigation.shared_with_me': FiCompass,
  'invitation.sent': FiMail,
}

const ACTION_COLORS: Record<string, string> = {
  'auth.login': 'green',
  'auth.logout': 'gray',
  'auth.password_changed': 'orange',
  'file.uploaded': 'blue',
  'file.renamed': 'purple',
  'file.moved': 'cyan',
  'file.deleted': 'red',
  'file.restored': 'teal',
  'file.viewed': 'blue',
  'file.shared': 'cyan',
  'file.tag_added': 'green',
  'folder.created': 'blue',
  'folder.renamed': 'purple',
  'folder.moved': 'cyan',
  'folder.deleted': 'red',
  'folder.visited': 'purple',
  'folder.shared': 'cyan',
  'chat.query': 'purple',
  'navigation.recently_viewed': 'gray',
  'navigation.favorites': 'gray',
  'navigation.shared_with_me': 'gray',
  'invitation.sent': 'orange',
}

function getActionIcon(action: string): React.ElementType {
  if (ACTION_ICONS[action]) return ACTION_ICONS[action]
  if (action.startsWith('file.')) return FiFile
  if (action.startsWith('folder.')) return FiFolder
  if (action.startsWith('auth.')) return FiLock
  if (action.startsWith('navigation.')) return FiCompass
  return FiActivity
}

function getActionColor(action: string): string {
  return ACTION_COLORS[action] || 'gray'
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function AuditLogContent({
  entries,
  isLoading,
  filters,
  onFilterChange,
  pagination,
  onLoadMore,
}: AuditLogContentProps) {
  const { t } = useAppTranslation('common')

  const getActionLabel = useCallback(
    (action: string): string => {
      const key = action.replace(/\./g, '_')
      return t(`profile.audit.actions.${key}`, { defaultValue: action })
    },
    [t]
  )

  const handleDateChange = useCallback(
    (field: 'date_from' | 'date_to', value: string) => {
      onFilterChange({ ...filters, [field]: value || undefined })
    },
    [filters, onFilterChange]
  )

  const handleActionChange = useCallback(
    (value: string) => {
      onFilterChange({ ...filters, action: value || undefined })
    },
    [filters, onFilterChange]
  )

  return (
    <VStack spacing={4} align="stretch">
      {/* Filter bar */}
      <Box
        bg="white"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.200"
        p={4}
      >
        <HStack spacing={3} flexWrap="wrap">
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              {t('profile.audit.from')}
            </Text>
            <input
              type="date"
              value={filters.date_from || ''}
              onChange={(e) => handleDateChange('date_from', e.target.value)}
              style={{
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              {t('profile.audit.to')}
            </Text>
            <input
              type="date"
              value={filters.date_to || ''}
              onChange={(e) => handleDateChange('date_to', e.target.value)}
              style={{
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              {t('profile.audit.actionType')}
            </Text>
            <Select
              size="sm"
              value={filters.action || ''}
              onChange={(e) => handleActionChange(e.target.value)}
              placeholder={t('profile.audit.allActions')}
              w="200px"
            >
              {ACTION_OPTIONS.map((action) => (
                <option key={action} value={action}>
                  {getActionLabel(action)}
                </option>
              ))}
            </Select>
          </Box>
        </HStack>
      </Box>

      {/* Timeline */}
      <Box
        bg="white"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.200"
        p={5}
        minH="300px"
      >
        {isLoading ? (
          <Center py={12}>
            <Spinner color="brand.500" />
          </Center>
        ) : entries.length === 0 ? (
          <Center py={12}>
            <VStack spacing={3}>
              <Icon as={FiActivity} boxSize={10} color="gray.300" />
              <Text color="gray.500" fontSize="sm">
                {t('profile.audit.noEntries')}
              </Text>
            </VStack>
          </Center>
        ) : (
          <Box position="relative">
            {entries.map((entry, i) => {
              const isLast = i === entries.length - 1
              const color = getActionColor(entry.action)
              const ActionIcon = getActionIcon(entry.action)
              return (
                <Box key={entry.id} display="flex" alignItems="flex-start">
                  {/* Node + vertical connector */}
                  <Box position="relative" flexShrink={0} mr={3} alignSelf="stretch">
                    <Box
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg={`${color}.100`}
                      border="2px solid"
                      borderColor={`${color}.200`}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      position="relative"
                      zIndex={1}
                    >
                      <Icon as={ActionIcon} boxSize={3.5} color={`${color}.600`} />
                    </Box>
                    {!isLast && (
                      <Box
                        position="absolute"
                        left="15px"
                        top="32px"
                        bottom={0}
                        w="2px"
                        bg="gray.200"
                      />
                    )}
                  </Box>

                  {/* Content */}
                  <Box flex={1} pb={isLast ? 2 : 6}>
                    <HStack justify="space-between" align="start">
                      <VStack align="start" spacing={0.5}>
                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                          {getActionLabel(entry.action)}
                        </Text>
                        {entry.metadata?.old_name && entry.metadata?.new_name ? (
                          <HStack spacing={1} flexWrap="wrap">
                            <Text fontSize="sm" color="gray.400" noOfLines={1} textDecoration="line-through">
                              {entry.metadata.old_name}
                            </Text>
                            <Text fontSize="sm" color="gray.400">→</Text>
                            <Text fontSize="sm" color="gray.600" fontWeight="medium" noOfLines={1}>
                              {entry.metadata.new_name}
                            </Text>
                          </HStack>
                        ) : entry.entity_name ? (
                          <Text fontSize="sm" color="gray.500" noOfLines={1}>
                            {entry.entity_name}
                          </Text>
                        ) : null}
                      </VStack>
                      <Text fontSize="xs" color="gray.400" flexShrink={0} mt={0.5}>
                        {formatRelativeTime(entry.created_at)}
                      </Text>
                    </HStack>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Box>

      {/* Load more */}
      {pagination.current_page < pagination.last_page && (
        <Center>
          <Button
            size="sm"
            variant="outline"
            isLoading={isLoading}
            onClick={onLoadMore}
          >
            {t('buttons.loadMore')}
          </Button>
        </Center>
      )}
    </VStack>
  )
}
